import memoize from 'moize'

import arriereBackoffice from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { FREE_PARKING_AMENITY_ID } from '@avantstay/backoffice-core'

import { hotelDetailsFragment } from '@arriere/modules/fragments/hotelInfo'
import { HotelUpdateProps } from '@arriere/types/Hotels.types'

// Fragments

export const amenitySelectionFragment = {
  homeId: true,
  amenityId: true,
  quantity: true,
  amenity: {
    id: true,
    name: true,
    amenityGroupId: true,
    amenityGroupName: true,
  },
}

// Queries

export const getHotelNamesById = memoize(
  async () => {
    const { list } = await arriereBackoffice.queries.hotel({
      __alias: 'getHotelNamesById',
      list: {
        hotel: {
          id: true,
          name: true,
        },
      },
    })

    return Object.fromEntries(list.map(({ hotel }) => [hotel.id, hotel.name]))
  },
  { isPromise: true, maxAge: 60 * 60 * 1000 },
)

export const getHotelFeaturedItems = async (hotelId: string) => {
  return arriereBackoffice.queries.hotel
    .raw({
      __alias: 'getHotelFeaturedItems',
      get: {
        __args: { hotelId },
        hotel: {
          ...hotelDetailsFragment,
          contentReferenceUnitId: true,
          featuredAmenities: {
            ...amenitySelectionFragment,
          },
          featuredItems: true,
          aggregatedAmenities: {
            roomTypeIds: true,
            hotelAmenity: {
              ...amenitySelectionFragment,
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length > 0) {
        throw new Error(errors[errors?.length]?.message)
      } else {
        return data.get?.hotel
      }
    })
}

export const getHotelRulesInfo = async (hotelId: string) => {
  return arriereBackoffice.queries.hotel
    .raw({
      __alias: 'getHotelRules',
      get: {
        __args: { hotelId },
        hotel: {
          contentReferenceUnitId: true,
          checkTime: {
            checkIn: true,
            checkOut: true,
          },
          petPolicy: {
            policyKind: true,
          },
          aggregatedAmenities: {
            hotelAmenity: {
              ...amenitySelectionFragment,
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length > 0) {
        throw new Error(errors[errors?.length]?.message)
      }

      return {
        contentReferenceUnitId: data.get?.hotel.contentReferenceUnitId,
        checkTime: data.get?.hotel.checkTime,
        petPolicy: data.get?.hotel.petPolicy?.policyKind,
        freeParking: data.get?.hotel.aggregatedAmenities?.find(
          amenityItem => amenityItem.hotelAmenity.amenityId === FREE_PARKING_AMENITY_ID,
        )?.hotelAmenity,
      }
    })
}

// Mutations

export const updateHotelData = ({ hotelId, data }: HotelUpdateProps) =>
  arriereBackoffice.mutations.hotel({
    __alias: 'updateHotelData',
    update: {
      __args: { hotelId, data },
      hotel: {
        id: true,
      },
    },
  })
